import { logRuntimeError } from 'Diagnostics/diagnostics';
import { AutocompleteType, type AutocompleteTypes } from 'Models/autocompleteItem.types';
import type { PreselectionEntry, SelectionEntry } from 'Models/facetRequest';
import { backupServerUrl, isLocalEnv, serverUrl, storeId, useDevServer } from 'Models/serverConfiguration';
import { type MainConfig, mainConfig } from 'Models/uiConfig/componentlessWidgetsConfig';
import platformHelpers from 'Models/uiConfig/platformHelpers';
import type {
  AutocompleteRequestDefaults,
  ConfigGetter,
  CustomerSummary,
  PlatformAddItemsToCart,
  RequestExtra,
  RuntimeConfigValue,
  SearchRequestDefaults as SearchRequestDefaultsShape,
  SearchRequestDefaultsFactory,
} from 'Models/uiConfig/requestTypes';
import { RELEVANCE_SORT } from 'Modules/serverApi/common/convert';
import { isFacetsResponseShape, isSearchResponseShape } from 'Modules/serverApi/common/validators';
import type { ServerModel } from 'Modules/serverApi/types';
import { isTermColor, isTermRanged } from 'Modules/termType';
import defaultRequestConfig from 'Platforms/shopify/requestConfig';
import type { Widget } from 'Templates/widgets';
import { isObjectRecord, isTruthy } from 'Utils/guards';
import { getFacetToggleState } from 'Utils/userStorage';

import FacetValue from '../facetValue';
import uiConfig from './uiConfig';

type PlatformLocalPreselectionGetter = ConfigGetter<
  [pageType: string, defaults?: RuntimeConfigValue],
  PreselectionEntry[] | null | undefined,
  RuntimeConfigValue
>;

export interface PlatformRequestConfig {
  addItemsToCart?: PlatformAddItemsToCart | null;
  facetResponseHandler?: ((response: ServerModel.FacetsResponse) => ServerModel.FacetsResponse) | null;
  getAutocompleteRequestDefaults?: (() => RequestExtra) | null;
  getCustomerAsync?: (() => Promise<CustomerSummary | null>) | null;
  getIgnoredUriParamNames?: (pageType: string) => string[];
  getLocalPreselection?: PlatformLocalPreselectionGetter | null;
  getSearchRequestDefaults?: SearchRequestDefaultsFactory | null;
  isAnalyticsEnabled?: boolean;
  isReferrerInUrl?: boolean | null;
  pageUriParam?: string | null;
  platformRoute?: string | null;
  requestTimeout?: number;
  responseHandler?:
    | ((response: ServerModel.SearchResponse, onInit?: () => void) => ServerModel.SearchResponse)
    | null;
}

const {
  autocomplete: autocompleteConfig = {},
  doAutocompleteRedirect,
  facetResponseHandler: customFacetResponseHandler,
  addItemsToCart: customAddItemsToCart,
  getIgnoredUriParamNames,
  getInitPreselection: customInitPreselectionGetter,
  getLocalPreselection: customLocalPreselectionGetter,
  getSearchRequestDefaults: customGetSearchRequestDefaults,
  isAnalyticsEnabled = true,
  isHtmxMode = false,
  maxCountByField = {},
  pageUriParam: customPageUriParam,
  requestTimeout: timeout = useDevServer ? 60000 : 10000,
  responseHandler: customResponseHandler,
  SearchRequestDefaults,
  Widgets,
} = mainConfig;

const defaults = defaultRequestConfig || {
  facetResponseHandler: null,
  addItemsToCart: null,
  getAutocompleteRequestDefaults: null,
  getCustomerAsync: null,
  getLocalPreselection: null,
  getSearchRequestDefaults: null,
  isReferrerInUrl: false,
  pageUriParam: null,
  platformRoute: null,
  responseHandler: null,
};

const pageUriParam = customPageUriParam || defaults.pageUriParam || 'page';

const autocompleteDefaultExtra = defaults.getAutocompleteRequestDefaults
  ? defaults.getAutocompleteRequestDefaults()
  : {};

if (autocompleteConfig.requestDefaults) {
  autocompleteConfig.requestDefaults.extra = {
    ...autocompleteDefaultExtra,
    ...(autocompleteConfig.requestDefaults.extra ?? {}),
  };
} else {
  autocompleteConfig.requestDefaults = {
    extra: autocompleteDefaultExtra,
  };
}

type AutocompleteConfig = Required<
  Pick<NonNullable<MainConfig['autocomplete']>, 'fields' | 'order' | 'facetValueOrder'>
> & {
  defaultOrder: AutocompleteTypes[];
  requestDefaults?: AutocompleteRequestDefaults;
};

const autocomplete: AutocompleteConfig = {
  fields: [],
  order: [],
  facetValueOrder: [],
  defaultOrder: [AutocompleteType.Text, AutocompleteType.Product, AutocompleteType.Facet],
  ...autocompleteConfig,
};

const localPreselection = getLocalPreselection();

const requestDefaults: SearchRequestDefaultsShape = {
  ...SearchRequestDefaults,
  ...getSearchRequestDefaults?.(uiConfig.pageType),
  extra: {
    ...SearchRequestDefaults?.extra,
    ...getSearchRequestDefaults?.(uiConfig.pageType)?.extra,
  },
};

const {
  pageSize: defaultPageSize,
  selection: defaultSelection = [],
  sort: defaultSort = RELEVANCE_SORT,
} = requestDefaults;

const hasSearchResults = Widgets.some((w) => w.visible && w.hasSearchResults);
const hasHtmxResults = Boolean(window.document.querySelector('[hx-trigger*="cm-htmx-update"]'));

const needInitRequest = Widgets.some((w) => w.needInitRequest);
const needInitItemRequest = Widgets.some(
  (w) =>
    w.visible &&
    ((w.type === 'RelatedItems' && w.getSelection) ||
      w.type === 'ProductData' ||
      w.type === 'RelatedColors' ||
      w.type === 'BuyItemsTogether'),
);

// stored via localStorage or Cookies or in config
const clientSideStoredFields = defaultSelection.map((f) => f.field);

const getCustomerAsync = defaults.getCustomerAsync || null;

const requestConfig = {
  autocomplete,
  backupServerUrl,
  clientSideStoredFields,
  defaultPageSize,
  defaultSelection,
  defaultSort,
  doAutocompleteRedirect,
  facetResponseHandler,
  addItemsToCart,
  getCustomerAsync,
  getIgnoredUriParamNames,
  hasHtmxResults,
  hasSearchResults,
  isAnalyticsEnabled(): boolean {
    return isAnalyticsEnabled && platformHelpers.getStoragePermissions().analytics === true;
  },
  isHtmxMode,
  isLocalEnv,
  isReferrerInUrl: defaults.isReferrerInUrl,
  getInitPreselection,
  localPreselection,
  maxCountByField,
  needInitItemRequest,
  needInitRequest,
  pageUriParam,
  platformRoute: defaults.platformRoute,
  requestDefaults,
  responseHandler,
  serverUrl,
  storeId,
  timeout,
  useDevServer,
};

window.Convermax.templates!.config.requestConfig = requestConfig;
export default requestConfig;

function getInitPreselection(): PreselectionEntry[] {
  const initPreselection = customInitPreselectionGetter
    ? (customInitPreselectionGetter(uiConfig.pageType)?.map((v) => ({
        ...v,
        term:
          v.noEscape || isTermRanged(v.term) || isTermColor(v.term)
            ? v.term
            : FacetValue.escapeTermValue(v.term),
      })) ?? [])
    : [];

  if (!hasSearchResults || initPreselection.some((value) => value.field === 'FilterByVehicle')) {
    return initPreselection;
  }

  const togglePreselection = Widgets.filter(
    (widget): widget is Widget<'FacetToggle' | 'SearchHeader' | 'VehicleWidget'> =>
      (widget.type === 'FacetToggle' || widget.type === 'SearchHeader' || widget.type === 'VehicleWidget') &&
      widget.visible,
  )
    .map((widget) => (widget.type === 'FacetToggle' ? widget : widget.facetToggleConfig))
    .filter((config): config is NonNullable<typeof config> => {
      if (!isTruthy(config)) {
        return false;
      }

      const { toggledFacet, initToggled, saveLastState } = config;
      if (toggledFacet?.field !== 'FilterByVehicle') {
        return false;
      }

      const stored = saveLastState ? getFacetToggleState(toggledFacet.field) : null;
      return stored ?? initToggled ?? false;
    })
    .map((config) => config.toggledFacet)
    .filter(isTruthy);

  return [...initPreselection, ...togglePreselection];
}

function getLocalPreselection(): SelectionEntry[] {
  const getPreselection = customLocalPreselectionGetter || defaults.getLocalPreselection;
  if (!getPreselection) {
    return [];
  }

  const defaultsPreselectionDefaults = getPreselectionDefaults(defaults.getLocalPreselection);
  let preselection: PreselectionEntry[] | null | undefined;
  try {
    preselection = getPreselection(uiConfig.pageType, defaultsPreselectionDefaults);
  } catch (error) {
    logRuntimeError({ source: 'requestConfig:getLocalPreselection', error });
    return [];
  }

  if (!Array.isArray(preselection)) {
    return [];
  }

  return preselection.flatMap((v): SelectionEntry[] => {
    if (!v || typeof v !== 'object' || typeof v.field !== 'string' || typeof v.term !== 'string') {
      logRuntimeError({
        source: 'requestConfig:getLocalPreselection:entry',
        error: new Error('preselection entry missing string `field` or `term`'),
      });
      return [];
    }
    return [
      {
        ...v,
        term:
          v.noEscape || isTermRanged(v.term) || isTermColor(v.term)
            ? v.term
            : FacetValue.escapeTermValue(v.term),
      },
    ];
  });
}

function addItemsToCart(ids: string[]): void {
  try {
    customAddItemsToCart?.(ids, defaults.addItemsToCart);
  } catch (error) {
    logRuntimeError({ source: 'requestConfig:addItemsToCart', error });
  }
}

/**
 * Invoke a merchant-supplied `responseHandler`-style hook with full
 * runtime safety: a thrown exception or a malformed return value never
 * crashes the request pipeline — both fall back to the input shape and
 * surface as a diagnostic event.
 */
function safeApplyHandler<T>(
  source: string,
  handler: (response: T) => T,
  predicate: (value: unknown) => value is T,
  response: T,
): T {
  let next: T;
  try {
    next = handler(response);
  } catch (error) {
    logRuntimeError({ source, error });
    return response;
  }
  if (!predicate(next)) {
    logRuntimeError({
      source,
      error: new Error('handler returned a value that is not a valid response shape'),
    });
    return response;
  }
  return next;
}

function responseHandler(
  response: ServerModel.SearchResponse,
  onInit?: () => void,
): ServerModel.SearchResponse {
  let processedResponse = response;

  if (defaults.responseHandler) {
    processedResponse = safeApplyHandler(
      'requestConfig:responseHandler:default',
      defaults.responseHandler,
      isSearchResponseShape,
      processedResponse,
    );
  }

  if (customResponseHandler) {
    processedResponse = safeApplyHandler(
      'requestConfig:responseHandler:custom',
      (r) => customResponseHandler(r, onInit),
      isSearchResponseShape,
      processedResponse,
    );
  }

  return processedResponse;
}

function facetResponseHandler(response: ServerModel.FacetsResponse): ServerModel.FacetsResponse {
  let processedResponse = response;

  if (defaults.facetResponseHandler) {
    processedResponse = safeApplyHandler(
      'requestConfig:facetResponseHandler:default',
      defaults.facetResponseHandler,
      isFacetsResponseShape,
      processedResponse,
    );
  }

  if (customFacetResponseHandler) {
    processedResponse = safeApplyHandler(
      'requestConfig:facetResponseHandler:custom',
      customFacetResponseHandler,
      isFacetsResponseShape,
      processedResponse,
    );
  }

  return processedResponse;
}

function safeCallSearchRequestDefaults(
  source: string,
  factory: SearchRequestDefaultsFactory,
  pageType: string,
): SearchRequestDefaultsShape | undefined {
  let result: SearchRequestDefaultsShape | undefined;
  try {
    result = factory(pageType);
  } catch (error) {
    logRuntimeError({ source, error });
    return undefined;
  }
  if (result === undefined || result === null) {
    return undefined;
  }
  if (!isObjectRecord(result)) {
    logRuntimeError({
      source,
      error: new Error('factory returned a non-object value'),
    });
    return undefined;
  }
  return result;
}

function getSearchRequestDefaults(pageType: string): SearchRequestDefaultsShape | undefined {
  let searchRequestDefaults: SearchRequestDefaultsShape | undefined;

  if (defaults.getSearchRequestDefaults) {
    const defaultSearchRequestDefaults = safeCallSearchRequestDefaults(
      'requestConfig:getSearchRequestDefaults:default',
      defaults.getSearchRequestDefaults,
      pageType,
    );
    searchRequestDefaults = {
      ...(searchRequestDefaults ?? {}),
      ...(defaultSearchRequestDefaults ?? {}),
      extra: {
        ...(searchRequestDefaults?.extra ?? {}),
        ...(defaultSearchRequestDefaults?.extra ?? {}),
      },
    };
  }

  if (customGetSearchRequestDefaults) {
    const customSearchRequestDefaults = safeCallSearchRequestDefaults(
      'requestConfig:getSearchRequestDefaults:custom',
      customGetSearchRequestDefaults,
      pageType,
    );
    searchRequestDefaults = {
      ...(searchRequestDefaults ?? {}),
      ...(customSearchRequestDefaults ?? {}),
      extra: {
        ...(searchRequestDefaults?.extra ?? {}),
        ...(customSearchRequestDefaults?.extra ?? {}),
      },
    };
  }

  return searchRequestDefaults;
}

function getPreselectionDefaults(
  getter: PlatformLocalPreselectionGetter | null | undefined,
): RuntimeConfigValue | undefined {
  // `ConfigGetter<..., ..., RuntimeConfigValue>` already types
  // `defaults` as `RuntimeConfigValue | undefined`; the prior `as`
  // cast was redundant noise that masked the actual contract.
  return getter?.defaults;
}
